<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                        @click="goBack"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">
                                                {{ badge?.name || 'Loading...' }}
                                        </h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Badge ID: {{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading badge...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <!-- Tabs -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Tab Headers -->
                                <div class="flex border-b border-gray-200 overflow-x-auto">
                                        <button
                                                v-for="tab in tabs"
                                                :key="tab.id"
                                                type="button"
                                                class="py-4 px-6 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                :class="{
                                                        'text-orange-7 border-orange-7 bg-orange-1': activeTab === tab.id,
                                                        'text-gray-500 border-transparent': activeTab !== tab.id
                                                }"
                                                @click="activeTab = tab.id"
                                        >
                                                {{ tab.label }}
                                        </button>
                                </div>

                                <!-- Tab Content -->
                                <div class="p-6">
                                        <!-- Properties Tab -->
                                        <div v-if="activeTab === 'properties'">
                                                <h3 class="text-base font-semibold text-gray-900 m-0 mb-4">Badge Properties</h3>
                                                <div class="max-w-lg mb-8">
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                        <div class="flex gap-2">
                                                                <input
                                                                        v-model="badgeName"
                                                                        type="text"
                                                                        class="flex-1 py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none"
                                                                >
                                                                <button
                                                                        type="button"
                                                                        :disabled="isSavingName || badgeName === badge?.name"
                                                                        class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
                                                                        @click="saveName"
                                                                >
                                                                        {{ isSavingName ? 'Saving...' : 'Save' }}
                                                                </button>
                                                        </div>
                                                </div>

                                                <!-- Badge Images Section -->
                                                <div class="border-t border-gray-200 pt-6">
                                                        <div class="flex justify-between items-center mb-4">
                                                                <h3 class="text-base font-semibold text-gray-900 m-0">Badge Images</h3>
                                                                <button
                                                                        type="button"
                                                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer"
                                                                        @click="showImageModal = true"
                                                                >
                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                        </svg>
                                                                        Add Image
                                                                </button>
                                                        </div>
                                                        <div v-if="badgeImages.length === 0" class="text-center py-8 px-4 bg-gray-50 rounded-lg">
                                                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                                <p class="text-gray-500 text-sm m-0">No badge images yet</p>
                                                        </div>
                                                        <div v-else class="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                                                                <div
                                                                        v-for="image in badgeImages"
                                                                        :key="image.id"
                                                                        class="bg-gray-50 rounded-lg p-4 relative"
                                                                >
                                                                        <img
                                                                                :src="image.url"
                                                                                alt="Badge Image"
                                                                                class="w-full h-32 object-contain rounded-md mb-2"
                                                                        >
                                                                        <div class="text-xs text-gray-500">
                                                                                <div v-if="image.regionid">Region: {{ getRegionName(image.regionid) }}</div>
                                                                                <div v-if="image.languageid">Language: {{ getLanguageName(image.languageid) }}</div>
                                                                                <div v-if="!image.regionid && !image.languageid" class="italic">Default (no region/language)</div>
                                                                        </div>
                                                                        <div class="absolute top-2 right-2 flex gap-1">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1 bg-white border border-gray-200 rounded cursor-pointer text-gray-500"
                                                                                        title="Edit"
                                                                                        @click="editImage(image)"
                                                                                >
                                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1 bg-white border border-gray-200 rounded cursor-pointer text-red-600"
                                                                                        title="Delete"
                                                                                        @click="deleteImage(image)"
                                                                                >
                                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Offerings Tab -->
                                        <div v-if="activeTab === 'offerings'">
                                                <div class="flex justify-between items-center mb-4">
                                                        <div>
                                                                <h3 class="text-base font-semibold text-gray-900 m-0">Offering Associations</h3>
                                                                <p class="text-sm text-gray-500 mt-1 mb-0">Drag offerings between panels or use the arrow buttons to associate them with this badge.</p>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                :disabled="!hasUnsavedOfferingChanges || isSavingOfferings"
                                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="saveOfferingChanges"
                                                        >
                                                                <svg v-if="isSavingOfferings" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                {{ isSavingOfferings ? 'Saving...' : 'Save Changes' }}
                                                                <span v-if="hasUnsavedOfferingChanges && !isSavingOfferings" class="w-2 h-2 bg-white rounded-full"></span>
                                                        </button>
                                                </div>
                                                <DualListSelector
                                                        :all-items="offerings"
                                                        :selected-ids="badgeOfferings"
                                                        item-label="offerings"
                                                        display-field="name"
                                                        secondary-field="size"
                                                        group-name="offerings"
                                                        @select="(item) => toggleOffering(item, true)"
                                                        @deselect="(item) => toggleOffering(item, false)"
                                                />
                                        </div>

                                        <!-- PDPs Tab -->
                                        <div v-if="activeTab === 'pdps'">
                                                <div class="flex justify-between items-center mb-4">
                                                        <div>
                                                                <h3 class="text-base font-semibold text-gray-900 m-0">PDP Associations</h3>
                                                                <p class="text-sm text-gray-500 mt-1 mb-0">Drag PDPs between panels or use the arrow buttons to associate them with this badge.</p>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                :disabled="!hasUnsavedPdpChanges || isSavingPdps"
                                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="savePdpChanges"
                                                        >
                                                                <svg v-if="isSavingPdps" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                {{ isSavingPdps ? 'Saving...' : 'Save Changes' }}
                                                                <span v-if="hasUnsavedPdpChanges && !isSavingPdps" class="w-2 h-2 bg-white rounded-full"></span>
                                                        </button>
                                                </div>
                                                <div v-if="isLoadingPdps" class="flex justify-center items-center py-16 px-4">
                                                        <svg class="w-8 h-8 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span class="ml-3 text-gray-500">Loading PDPs...</span>
                                                </div>
                                                <template v-else>
                                                        <DualListSelector
                                                                :all-items="pdps"
                                                                :selected-ids="badgePdps"
                                                                item-label="PDPs"
                                                                display-field="name"
                                                                group-name="pdps"
                                                                @select="(item) => togglePdp(item, true)"
                                                                @deselect="(item) => togglePdp(item, false)"
                                                        />
                                                </template>
                                        </div>

                                        <!-- Lister Items Tab -->
                                        <div v-if="activeTab === 'listeritems'">
                                                <div class="flex justify-between items-center mb-4">
                                                        <div>
                                                                <h3 class="text-base font-semibold text-gray-900 m-0">Lister Item Associations</h3>
                                                                <p class="text-sm text-gray-500 mt-1 mb-0">Drag lister items between panels or use the arrow buttons to associate them with this badge.</p>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                :disabled="!hasUnsavedListerItemChanges || isSavingListerItems"
                                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="saveListerItemChanges"
                                                        >
                                                                <svg v-if="isSavingListerItems" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                {{ isSavingListerItems ? 'Saving...' : 'Save Changes' }}
                                                                <span v-if="hasUnsavedListerItemChanges && !isSavingListerItems" class="w-2 h-2 bg-white rounded-full"></span>
                                                        </button>
                                                </div>
                                                <div v-if="isLoadingListerItems" class="flex justify-center items-center py-16 px-4">
                                                        <svg class="w-8 h-8 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span class="ml-3 text-gray-500">Loading lister items...</span>
                                                </div>
                                                <template v-else>
                                                        <DualListSelector
                                                                :all-items="listerItems"
                                                                :selected-ids="badgeListerItems"
                                                                item-label="lister items"
                                                                display-field="name"
                                                                group-name="listeritems"
                                                                @select="(item) => toggleListerItem(item, true)"
                                                                @deselect="(item) => toggleListerItem(item, false)"
                                                        />
                                                </template>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Image Modal -->
                <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeImageModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">
                                                {{ editingImage ? 'Edit Badge Image' : 'Add Badge Image' }}
                                        </h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="closeImageModal"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <!-- Region -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Region (optional)</label>
                                                <select
                                                        v-model="imageForm.regionid"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none"
                                                >
                                                        <option :value="null">None</option>
                                                        <option v-for="region in regions" :key="region.id" :value="region.id">
                                                                {{ region.id }} | {{ region.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <!-- Language -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Language (optional)</label>
                                                <select
                                                        v-model="imageForm.languageid"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none"
                                                >
                                                        <option :value="null">None</option>
                                                        <option v-for="lang in languages" :key="lang.id" :value="lang.id">
                                                                {{ lang.id }} | {{ lang.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <!-- Image Upload -->
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Image (SVG only)</label>
                                                <input
                                                        ref="fileInputRef"
                                                        type="file"
                                                        accept=".svg,image/svg+xml"
                                                        class="hidden"
                                                        @change="handleFileSelect"
                                                >
                                                <button
                                                        type="button"
                                                        class="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg bg-transparent cursor-pointer text-center text-gray-500"
                                                        :disabled="isUploadingFile"
                                                        @click="openFilePicker"
                                                >
                                                        <svg v-if="!isUploadingFile" class="w-6 h-6 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <svg v-else class="w-6 h-6 mx-auto mb-2 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                        </svg>
                                                        {{ isUploadingFile ? 'Uploading...' : 'Click to upload SVG image' }}
                                                </button>
                                                <div v-if="imageForm.url" class="mt-3 flex items-center justify-between p-2 bg-gray-50 rounded-md">
                                                        <img :src="imageForm.url" alt="Preview" class="w-16 h-16 object-contain rounded">
                                                        <button
                                                                type="button"
                                                                class="text-red-600 bg-transparent border-none cursor-pointer p-1"
                                                                @click="imageForm.url = ''"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="closeImageModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!imageForm.url || isSavingImage"
                                                class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50"
                                                @click="saveImage"
                                        >
                                                {{ isSavingImage ? 'Saving...' : 'Save' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onUnmounted, reactive } from 'vue';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import Swal from 'sweetalert2';
import BadgeService from 'services/BadgeService';
import BadgeImageService from 'services/BadgeImageService';
import BadgeOfferingService from 'services/BadgeOfferingService';
import BadgePDPService from 'services/BadgePDPService';
import BadgeProductCategoryService from 'services/BadgeProductCategoryService';
import OfferingService from 'services/OfferingService';
import PDPService from 'services/PDPService';
import ProductCategoryService from 'services/ProductCategoryService';
import RegionService from 'services/RegionService';
import LanguageService from 'services/LanguageService';
import { showDeleteConfirmation } from 'utils/swal';
import { httpClient } from 'utils/http';
import DualListSelector from 'components/DualListSelector.vue';

export default defineComponent({
        name: 'EditBadgePage',
        components: {
                DualListSelector,
        },
        setup() {
                const router = useRouter();
                const route = useRoute();

                const routeId = computed(() => route.params.id);

                const badge = ref(null);
                const badgeName = ref('');
                const badgeImages = ref([]);
                const isLoading = ref(true);
                const isSavingName = ref(false);
                const isSavingImage = ref(false);

                const activeTab = ref('properties');
                const tabs = [
                        { id: 'properties', label: 'Properties' },
                        { id: 'listeritems', label: 'Lister Items' },
                        { id: 'pdps', label: 'PDPs' },
                        { id: 'offerings', label: 'Offerings' },
                ];

                const regions = ref([]);
                const languages = ref([]);
                const offerings = ref([]);
                const pdps = ref([]);
                const listerItems = ref([]);
                const isLoadingPdps = ref(false);
                const isLoadingListerItems = ref(false);

                const badgeOfferings = ref({});
                const badgePdps = ref({});
                const badgeListerItems = ref({});

                // Track original state for detecting changes
                const originalBadgeOfferings = ref({});
                const originalBadgePdps = ref({});
                const originalBadgeListerItems = ref({});

                // Track pending changes (not yet saved)
                const pendingOfferingsToAdd = ref(new Set());
                const pendingOfferingsToRemove = ref(new Set());
                const pendingPdpsToAdd = ref(new Set());
                const pendingPdpsToRemove = ref(new Set());
                const pendingListerItemsToAdd = ref(new Set());
                const pendingListerItemsToRemove = ref(new Set());

                // Saving state
                const isSavingOfferings = ref(false);
                const isSavingPdps = ref(false);
                const isSavingListerItems = ref(false);

                // Computed properties for unsaved changes
                const hasUnsavedOfferingChanges = computed(() => 
                        pendingOfferingsToAdd.value.size > 0 || pendingOfferingsToRemove.value.size > 0
                );
                const hasUnsavedPdpChanges = computed(() => 
                        pendingPdpsToAdd.value.size > 0 || pendingPdpsToRemove.value.size > 0
                );
                const hasUnsavedListerItemChanges = computed(() => 
                        pendingListerItemsToAdd.value.size > 0 || pendingListerItemsToRemove.value.size > 0
                );

                // Check if there are any unsaved changes across all tabs
                const hasAnyUnsavedChanges = computed(() => 
                        hasUnsavedOfferingChanges.value || 
                        hasUnsavedPdpChanges.value || 
                        hasUnsavedListerItemChanges.value
                );

                const offeringSearch = ref('');
                const pdpSearch = ref('');
                const listerItemSearch = ref('');

                const showImageModal = ref(false);
                const editingImage = ref(null);
                const fileInputRef = ref(null);
                const isUploadingFile = ref(false);
                const imageForm = reactive({
                        regionid: null,
                        languageid: null,
                        url: '',
                });

                const toastMessage = ref('');
                const toastVariant = ref('success');
                let toastTimeout;

                const showToast = (message, variant = 'success') => {
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 3000);
                };

                const filteredOfferings = computed(() => {
                        if (!offeringSearch.value.trim()) return offerings.value;
                        const query = offeringSearch.value.toLowerCase();
                        return offerings.value.filter(o =>
                                o.name?.toLowerCase().includes(query) ||
                                o.externalid?.toLowerCase().includes(query) ||
                                String(o.id).includes(query)
                        );
                });

                const filteredPdps = computed(() => {
                        if (!pdpSearch.value.trim()) return pdps.value;
                        const query = pdpSearch.value.toLowerCase();
                        return pdps.value.filter(p =>
                                p.name?.toLowerCase().includes(query) ||
                                String(p.id).includes(query)
                        );
                });

                const filteredListerItems = computed(() => {
                        if (!listerItemSearch.value.trim()) return listerItems.value;
                        const query = listerItemSearch.value.toLowerCase();
                        return listerItems.value.filter(i =>
                                i.name?.toLowerCase().includes(query) ||
                                String(i.id).includes(query)
                        );
                });

                const getRegionName = (id) => {
                        const region = regions.value.find(r => r.id === id);
                        return region ? region.name : `Region ${id}`;
                };

                const getLanguageName = (id) => {
                        const lang = languages.value.find(l => l.id === id);
                        return lang ? lang.name : `Language ${id}`;
                };

                const goBack = () => {
                        router.push('/badges');
                };

                const loadBadge = async () => {
                        isLoading.value = true;
                        try {
                                badge.value = await BadgeService.get(routeId.value);
                                badgeName.value = badge.value.name || '';
                        } catch (error) {
                                showToast(error.message || 'Failed to load badge', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadBadgeImages = async () => {
                        try {
                                const params = new URLSearchParams({
                                        where: JSON.stringify({ badgeid: routeId.value }),
                                        limit: '0',
                                });
                                badgeImages.value = await BadgeImageService.getAll(params);
                        } catch (error) {
                                showToast(error.message || 'Failed to load badge images', 'error');
                        }
                };

                const loadReferenceData = async () => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const [regionsData, languagesData] = await Promise.all([
                                        RegionService.getAll(params),
                                        LanguageService.getAll(params),
                                ]);
                                regions.value = regionsData;
                                languages.value = languagesData;
                        } catch (error) {
                                console.error('Failed to load reference data:', error);
                        }
                };

                const loadOfferings = async () => {
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const [offeringsData, badgeOfferingsData] = await Promise.all([
                                        OfferingService.getAll(params),
                                        BadgeOfferingService.getAll(new URLSearchParams({
                                                where: JSON.stringify({ badgeid: routeId.value }),
                                                limit: '0',
                                        })),
                                ]);
                                offerings.value = offeringsData;
                                const offeringsMap = Object.fromEntries(
                                        badgeOfferingsData.map(item => [item.offeringid, item])
                                );
                                badgeOfferings.value = offeringsMap;
                                originalBadgeOfferings.value = { ...offeringsMap };
                                // Reset pending changes
                                pendingOfferingsToAdd.value = new Set();
                                pendingOfferingsToRemove.value = new Set();
                        } catch (error) {
                                showToast(error.message || 'Failed to load offerings', 'error');
                        }
                };

                const loadPdps = async () => {
                        isLoadingPdps.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const [pdpsData, badgePdpsData] = await Promise.all([
                                        PDPService.getAll(params),
                                        BadgePDPService.getAll(new URLSearchParams({
                                                where: JSON.stringify({ badgeid: routeId.value }),
                                                limit: '0',
                                        })),
                                ]);
                                pdps.value = pdpsData;
                                const pdpsMap = Object.fromEntries(
                                        badgePdpsData.map(item => [item.pdpid, item])
                                );
                                badgePdps.value = pdpsMap;
                                originalBadgePdps.value = { ...pdpsMap };
                                // Reset pending changes
                                pendingPdpsToAdd.value = new Set();
                                pendingPdpsToRemove.value = new Set();
                        } catch (error) {
                                showToast(error.message || 'Failed to load PDPs', 'error');
                        } finally {
                                isLoadingPdps.value = false;
                        }
                };

                const loadListerItems = async () => {
                        isLoadingListerItems.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const [listerItemsData, badgeListerItemsData] = await Promise.all([
                                        ProductCategoryService.getAll(params),
                                        BadgeProductCategoryService.getAll(new URLSearchParams({
                                                where: JSON.stringify({ badgeid: routeId.value }),
                                                limit: '0',
                                        })),
                                ]);
                                listerItems.value = listerItemsData;
                                const listerItemsMap = Object.fromEntries(
                                        badgeListerItemsData.map(item => [item.productcategoryid, item])
                                );
                                badgeListerItems.value = listerItemsMap;
                                originalBadgeListerItems.value = { ...listerItemsMap };
                                // Reset pending changes
                                pendingListerItemsToAdd.value = new Set();
                                pendingListerItemsToRemove.value = new Set();
                        } catch (error) {
                                showToast(error.message || 'Failed to load lister items', 'error');
                        } finally {
                                isLoadingListerItems.value = false;
                        }
                };

                const saveName = async () => {
                        if (!badgeName.value.trim() || badgeName.value === badge.value?.name) return;
                        
                        isSavingName.value = true;
                        try {
                                await BadgeService.update(routeId.value, { name: badgeName.value.trim() });
                                badge.value.name = badgeName.value.trim();
                                showToast('Badge name updated successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to update badge name', 'error');
                        } finally {
                                isSavingName.value = false;
                        }
                };

                const openFilePicker = () => {
                        fileInputRef.value?.click();
                };

                const handleFileSelect = async (event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;

                        if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
                                showToast('Please select an SVG file', 'error');
                                event.target.value = '';
                                return;
                        }

                        isUploadingFile.value = true;
                        try {
                                const formData = new FormData();
                                formData.append('file', file);

                                const response = await httpClient.post('/api/upload', formData, {
                                        headers: {
                                                'Content-Type': 'multipart/form-data',
                                        },
                                });

                                if (response.data?.url) {
                                        imageForm.url = response.data.url;
                                } else if (typeof response.data === 'string') {
                                        imageForm.url = response.data;
                                } else {
                                        showToast('Upload succeeded but no URL returned', 'error');
                                }
                        } catch (error) {
                                showToast(error.response?.data?.message || error.message || 'Failed to upload file', 'error');
                        } finally {
                                isUploadingFile.value = false;
                                event.target.value = '';
                        }
                };

                const closeImageModal = () => {
                        showImageModal.value = false;
                        editingImage.value = null;
                        imageForm.regionid = null;
                        imageForm.languageid = null;
                        imageForm.url = '';
                };

                const editImage = (image) => {
                        editingImage.value = image;
                        imageForm.regionid = image.regionid;
                        imageForm.languageid = image.languageid;
                        imageForm.url = image.url;
                        showImageModal.value = true;
                };

                const saveImage = async () => {
                        if (!imageForm.url) return;

                        isSavingImage.value = true;
                        try {
                                if (editingImage.value) {
                                        await BadgeImageService.update(editingImage.value.id, {
                                                regionid: imageForm.regionid,
                                                languageid: imageForm.languageid,
                                                url: imageForm.url,
                                        });
                                        showToast('Badge image updated successfully');
                                } else {
                                        const created = await BadgeImageService.create({
                                                badgeid: routeId.value,
                                                regionid: imageForm.regionid,
                                                languageid: imageForm.languageid,
                                        });
                                        await BadgeImageService.update(created.id, {
                                                url: imageForm.url,
                                        });
                                        showToast('Badge image added successfully');
                                }
                                closeImageModal();
                                await loadBadgeImages();
                        } catch (error) {
                                showToast(error.message || 'Failed to save badge image', 'error');
                        } finally {
                                isSavingImage.value = false;
                        }
                };

                const deleteImage = async (image) => {
                        const confirmed = await showDeleteConfirmation('Are you sure you want to delete this badge image?');
                        if (!confirmed) return;

                        try {
                                await BadgeImageService.delete(image.id);
                                showToast('Badge image deleted successfully');
                                await loadBadgeImages();
                        } catch (error) {
                                showToast(error.message || 'Failed to delete badge image', 'error');
                        }
                };

                // Local toggle functions - track changes without saving to server
                const toggleOffering = (offering, checked) => {
                        const id = offering.id;
                        const wasOriginallySelected = !!originalBadgeOfferings.value[id];
                        
                        if (checked) {
                                // Adding
                                badgeOfferings.value[id] = { offeringid: id };
                                pendingOfferingsToRemove.value.delete(id);
                                if (!wasOriginallySelected) {
                                        pendingOfferingsToAdd.value.add(id);
                                }
                        } else {
                                // Removing
                                delete badgeOfferings.value[id];
                                pendingOfferingsToAdd.value.delete(id);
                                if (wasOriginallySelected) {
                                        pendingOfferingsToRemove.value.add(id);
                                }
                        }
                        // Trigger reactivity
                        pendingOfferingsToAdd.value = new Set(pendingOfferingsToAdd.value);
                        pendingOfferingsToRemove.value = new Set(pendingOfferingsToRemove.value);
                };

                const togglePdp = (pdp, checked) => {
                        const id = pdp.id;
                        const wasOriginallySelected = !!originalBadgePdps.value[id];
                        
                        if (checked) {
                                badgePdps.value[id] = { pdpid: id };
                                pendingPdpsToRemove.value.delete(id);
                                if (!wasOriginallySelected) {
                                        pendingPdpsToAdd.value.add(id);
                                }
                        } else {
                                delete badgePdps.value[id];
                                pendingPdpsToAdd.value.delete(id);
                                if (wasOriginallySelected) {
                                        pendingPdpsToRemove.value.add(id);
                                }
                        }
                        pendingPdpsToAdd.value = new Set(pendingPdpsToAdd.value);
                        pendingPdpsToRemove.value = new Set(pendingPdpsToRemove.value);
                };

                const toggleListerItem = (item, checked) => {
                        const id = item.id;
                        const wasOriginallySelected = !!originalBadgeListerItems.value[id];
                        
                        if (checked) {
                                badgeListerItems.value[id] = { productcategoryid: id };
                                pendingListerItemsToRemove.value.delete(id);
                                if (!wasOriginallySelected) {
                                        pendingListerItemsToAdd.value.add(id);
                                }
                        } else {
                                delete badgeListerItems.value[id];
                                pendingListerItemsToAdd.value.delete(id);
                                if (wasOriginallySelected) {
                                        pendingListerItemsToRemove.value.add(id);
                                }
                        }
                        pendingListerItemsToAdd.value = new Set(pendingListerItemsToAdd.value);
                        pendingListerItemsToRemove.value = new Set(pendingListerItemsToRemove.value);
                };

                // Save functions - batch save all pending changes
                const saveOfferingChanges = async () => {
                        if (!hasUnsavedOfferingChanges.value) return;
                        
                        isSavingOfferings.value = true;
                        try {
                                // Delete removed offerings
                                for (const id of pendingOfferingsToRemove.value) {
                                        const existing = originalBadgeOfferings.value[id];
                                        if (existing) {
                                                await BadgeOfferingService.delete(existing.id);
                                        }
                                }
                                
                                // Add new offerings
                                for (const id of pendingOfferingsToAdd.value) {
                                        const created = await BadgeOfferingService.create({
                                                badgeid: routeId.value,
                                                offeringid: id,
                                        });
                                        badgeOfferings.value[id] = created;
                                }
                                
                                // Update original state
                                originalBadgeOfferings.value = { ...badgeOfferings.value };
                                pendingOfferingsToAdd.value = new Set();
                                pendingOfferingsToRemove.value = new Set();
                                
                                showToast('Offering associations saved successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to save offering changes', 'error');
                        } finally {
                                isSavingOfferings.value = false;
                        }
                };

                const savePdpChanges = async () => {
                        if (!hasUnsavedPdpChanges.value) return;
                        
                        isSavingPdps.value = true;
                        try {
                                // Delete removed PDPs
                                for (const id of pendingPdpsToRemove.value) {
                                        const existing = originalBadgePdps.value[id];
                                        if (existing) {
                                                await BadgePDPService.delete(existing.id);
                                        }
                                }
                                
                                // Add new PDPs
                                for (const id of pendingPdpsToAdd.value) {
                                        const created = await BadgePDPService.create({
                                                badgeid: routeId.value,
                                                pdpid: id,
                                        });
                                        badgePdps.value[id] = created;
                                }
                                
                                // Update original state
                                originalBadgePdps.value = { ...badgePdps.value };
                                pendingPdpsToAdd.value = new Set();
                                pendingPdpsToRemove.value = new Set();
                                
                                showToast('PDP associations saved successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to save PDP changes', 'error');
                        } finally {
                                isSavingPdps.value = false;
                        }
                };

                const saveListerItemChanges = async () => {
                        if (!hasUnsavedListerItemChanges.value) return;
                        
                        isSavingListerItems.value = true;
                        try {
                                // Delete removed lister items
                                for (const id of pendingListerItemsToRemove.value) {
                                        const existing = originalBadgeListerItems.value[id];
                                        if (existing) {
                                                await BadgeProductCategoryService.delete(existing.id);
                                        }
                                }
                                
                                // Add new lister items
                                for (const id of pendingListerItemsToAdd.value) {
                                        const created = await BadgeProductCategoryService.create({
                                                badgeid: routeId.value,
                                                productcategoryid: id,
                                        });
                                        badgeListerItems.value[id] = created;
                                }
                                
                                // Update original state
                                originalBadgeListerItems.value = { ...badgeListerItems.value };
                                pendingListerItemsToAdd.value = new Set();
                                pendingListerItemsToRemove.value = new Set();
                                
                                showToast('Lister item associations saved successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to save lister item changes', 'error');
                        } finally {
                                isSavingListerItems.value = false;
                        }
                };

                watch(activeTab, (newTab) => {
                        if (newTab === 'offerings' && offerings.value.length === 0) {
                                loadOfferings();
                        } else if (newTab === 'pdps' && pdps.value.length === 0) {
                                loadPdps();
                        } else if (newTab === 'listeritems' && listerItems.value.length === 0) {
                                loadListerItems();
                        }
                });

                onMounted(async () => {
                        await Promise.all([
                                loadBadge(),
                                loadBadgeImages(),
                                loadReferenceData(),
                        ]);
                });

                // Browser beforeunload handler for tab close/external navigation
                const handleBeforeUnload = (e) => {
                        if (hasAnyUnsavedChanges.value) {
                                e.preventDefault();
                                e.returnValue = '';
                                return '';
                        }
                };

                onMounted(() => {
                        window.addEventListener('beforeunload', handleBeforeUnload);
                });

                onUnmounted(() => {
                        window.removeEventListener('beforeunload', handleBeforeUnload);
                });

                // Vue Router navigation guard
                onBeforeRouteLeave(async (to, from, next) => {
                        if (hasAnyUnsavedChanges.value) {
                                const result = await Swal.fire({
                                        title: 'Unsaved Changes',
                                        text: 'You have unsaved changes. Are you sure you want to leave this page?',
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonColor: '#FF6600',
                                        cancelButtonColor: '#6b7280',
                                        confirmButtonText: 'Leave Page',
                                        cancelButtonText: 'Stay',
                                });
                                if (result.isConfirmed) {
                                        next();
                                } else {
                                        next(false);
                                }
                        } else {
                                next();
                        }
                });

                return {
                        routeId,
                        badge,
                        badgeName,
                        badgeImages,
                        isLoading,
                        isSavingName,
                        isSavingImage,
                        activeTab,
                        tabs,
                        regions,
                        languages,
                        offerings,
                        pdps,
                        listerItems,
                        isLoadingPdps,
                        isLoadingListerItems,
                        badgeOfferings,
                        badgePdps,
                        badgeListerItems,
                        offeringSearch,
                        pdpSearch,
                        listerItemSearch,
                        showImageModal,
                        editingImage,
                        imageForm,
                        toastMessage,
                        toastVariant,
                        filteredOfferings,
                        filteredPdps,
                        filteredListerItems,
                        getRegionName,
                        getLanguageName,
                        goBack,
                        saveName,
                        openFilePicker,
                        handleFileSelect,
                        fileInputRef,
                        isUploadingFile,
                        closeImageModal,
                        editImage,
                        saveImage,
                        deleteImage,
                        toggleOffering,
                        togglePdp,
                        toggleListerItem,
                        // Save functions for associations
                        saveOfferingChanges,
                        savePdpChanges,
                        saveListerItemChanges,
                        // Saving state
                        isSavingOfferings,
                        isSavingPdps,
                        isSavingListerItems,
                        // Unsaved changes indicators
                        hasUnsavedOfferingChanges,
                        hasUnsavedPdpChanges,
                        hasUnsavedListerItemChanges,
                };
        },
});
</script>

<style>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
